import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { GameSettings } from '../types/game';
import questionService from '../services/questionService';
import gameService from '../services/gameService';
import authService from '../services/authService';
// TODO: Re-enable when favorites API is implemented
// import SaveToFavorites from '../components/SaveToFavorites';
// ChatGPT integration is now active!
// import ChatWidget from '../components/ChatWidget';
import ChatWidget from '../components/ChatWidget';
import { calculateAnswerPoints } from '../utils/scoring';
import { Pause } from 'lucide-react';
import './next-question-button.css';
import './timer-styles.css';
import './bible-reference-styles.css';
import '../components/HomeReturnButton.css';
import './trivia-mobile.css';
import './trivia-desktop.css';
import './auto-progress-indicator.css';

// Add some basic styles for loading states
const loadingStyles = `
  .loading-text {
    color: #888;
    font-style: italic;
  }

  .loading-options .loading-button {
    background: #f5f5f5;
    color: #888;
    border: 1px solid #ddd;
    cursor: not-allowed;
    opacity: 0.7;
  }

  .loading-options .loading-button:hover {
    background: #f5f5f5;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = loadingStyles;
  document.head.appendChild(style);
}

// Temporary fallback mock questions in case API fails
const mockQuestions = [
  {
    id: '1',
    text: 'Who built the ark?',
    options: ['Moses', 'Noah', 'Abraham', 'David'],
    correctAnswer: 'Noah',
    difficulty: 'easy',
    category: 'old-testament',
    bibleReference: 'Genesis 6:14'
  },
  {
    id: '2',
    text: 'How many disciples did Jesus have?',
    options: ['10', '12', '13', '7'],
    correctAnswer: '12',
    difficulty: 'easy',
    category: 'new-testament',
    bibleReference: 'Matthew 10:1-4'
  },
  {
    id: '3',
    text: 'Who was thrown into the lions\' den?',
    options: ['Daniel', 'Jonah', 'Elijah', 'Isaiah'],
    correctAnswer: 'Daniel',
    difficulty: 'medium',
    category: 'old-testament',
    bibleReference: 'Daniel 6:16-23'
  },
  {
    id: '4',
    text: 'What was the first miracle Jesus performed?',
    options: ['Feeding the 5,000', 'Healing a leper', 'Turning water into wine', 'Walking on water'],
    correctAnswer: 'Turning water into wine',
    difficulty: 'medium',
    category: 'new-testament',
    bibleReference: 'John 2:1-11'
  },
  {
    id: '5',
    text: 'Which prophet was swallowed by a great fish?',
    options: ['Jonah', 'Hosea', 'Amos', 'Micah'],
    correctAnswer: 'Jonah',
    difficulty: 'easy',
    category: 'old-testament',
    bibleReference: 'Jonah 1:17'
  }
];

const TriviaGamePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated: auth0Authenticated, getAccessTokenSilently } = useAuth0();

  // Use Auth0 authentication state as primary, fallback to our service
  const isAuthenticated = auth0Authenticated || authService.isAuthenticated();
  
  // Parse URL parameters to handle direct navigation (e.g., /play?category=timeline)
  const urlParams = new URLSearchParams(location.search);
  const urlCategory = urlParams.get('category');
  
  const settings = useMemo(() => location.state?.settings as GameSettings || {
    difficulty: 'medium',
    category: urlCategory || 'all', // Use URL category if available
    questionsCount: 15, // Fixed at 15 questions
    timePerQuestion: 15, // Fixed at 15 seconds
    autoProgress: true // Enable auto-progress by default
  }, [location.state, urlCategory]);
  
  console.log('TriviaGamePage settings:', settings);
  
  const [questions, setQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // No session tracking needed - production API uses simple results submission
  const [gameState, setGameState] = useState<any>({
    currentQuestionIndex: 0,
    score: 0,
    timeRemaining: settings.timePerQuestion,
    answers: [],
    gameStatus: 'playing',
    selectedAnswer: null,
    timedOut: false,
    currentStreak: 0,
    maxStreak: 0
  });
  
  // Ref to store the auto-progression timeout
  const autoProgressTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoProgressTimeoutRef.current) {
        clearTimeout(autoProgressTimeoutRef.current);
      }
    };
  }, []);
  
  // Fetch questions from API
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        
        // Map difficulty levels from frontend to backend
        const difficultyMap: Record<string, string> = {
          'easy': 'beginner',
          'medium': 'intermediate',
          'hard': 'advanced',
          'mixed': '' // Empty string means no difficulty filter
        };
        
        // Map categories from frontend to backend
        const categoryMap: Record<string, string[]> = {
          'all': [],
          'old-testament': ['Old Testament'],
          'new-testament': ['New Testament'],
          'gospels': ['Gospels'],
          'epistles': ['Epistles'],
          'timeline': ['timeline']
        };
        
        // Prepare request parameters
        const requestParams: {
          difficulty: string;
          difficulties?: string[];
          categories: string[];
          count: number;
          excludeIds: string[];
          type?: string;
        } = {
          difficulty: difficultyMap[settings.difficulty] || '',
          categories: categoryMap[settings.category] || [],
          count: settings.questionsCount,
          excludeIds: []
        };
        
        // Handle mixed difficulty specially
        if (settings.difficulty === 'mixed') {
          // For mixed difficulty, we'll explicitly request questions from all difficulty levels
          requestParams.difficulty = ''; // Clear the single difficulty
          requestParams.difficulties = ['beginner', 'intermediate', 'advanced']; // Add all difficulties
          console.log('Fetching mixed difficulty questions with params:', requestParams);
        }
        
        console.log('Fetching questions with params:', requestParams);
        
        // Handle scenarios category specially
        if (settings.category === 'scenarios') {
          // Remove category filter and add type filter instead
          requestParams.categories = [];
          requestParams.type = 'scenario';
          // Important: Remove the default difficulty if it doesn't find enough questions
          if (settings.difficulty === 'mixed') {
            requestParams.difficulty = '';
            requestParams.difficulties = ['beginner', 'intermediate', 'advanced'];
          }
          console.log('Fetching scenario questions with params:', requestParams);
        }
        
        // Fetch questions based on authentication status
        let apiQuestions;
        
        if (isAuthenticated && user && settings.category !== 'scenarios') {
          // For authenticated users: Use rank-based progression (except scenarios which need special handling)
          try {
            console.log('🎯 Fetching progression questions for authenticated user...');
            apiQuestions = await questionService.getQuestionsForUser(settings.questionsCount);
            console.log('✅ Progression API response received:', apiQuestions);
          } catch (error) {
            console.warn('❌ Progression API failed, falling back to random questions:', error);
            // Fallback to website API if progression fails
            apiQuestions = await questionService.getRandomQuestions(requestParams);
            console.log('🔄 Fallback API response received:', apiQuestions);
          }
        } else {
          // For guests or scenarios: Use website API with shuffled answers
          console.log('🎲 Fetching random questions from website API...');
          apiQuestions = await questionService.getRandomQuestions(requestParams);
          console.log('✅ Random API response received:', apiQuestions);
        }
        
        if (apiQuestions && apiQuestions.length > 0) {
          // Transform API questions to the format expected by the component
          const formattedQuestions = apiQuestions.map(q => {
            // Handle different question formats - cast to any to handle legacy format
            const qAny = q as any;
            let formattedOptions: string[] | {text: string; isCorrect: boolean}[];
            let correctAnswer = '';
            
            // If options are strings (old format), convert them
            if (Array.isArray(qAny.options) && typeof qAny.options[0] === 'string') {
              formattedOptions = qAny.options as string[];
              // Get correct answer by index or by matching text
              if (typeof qAny.correctAnswer === 'number') {
                correctAnswer = (qAny.options as string[])[qAny.correctAnswer] || '';
              } else {
                correctAnswer = qAny.correctAnswer || '';
              }
            } else {
              // New format with option objects
              formattedOptions = q.options;
              correctAnswer = q.options.find(opt => opt.isCorrect)?.text || '';
            }
            
            // Base question properties
            const formattedQuestion = {
              id: q._id,
              text: q.text,
              options: formattedOptions,
              correctAnswer: correctAnswer,
              difficulty: q.difficulty,
              category: (q.categories && q.categories[0]) || '',
              bibleReference: q.bibleReference,
              explanation: q.explanation,
              type: q.type,
              points: q.points
            };
            
            // Add scenario details if applicable
            if (q.type === 'scenario' && q.scenarioDetails) {
              (formattedQuestion as any).scenarioDetails = q.scenarioDetails;
            }
            
            return formattedQuestion;
          });
          
          setQuestions(formattedQuestions);
        } else {
          // Fallback to mock questions if API returns empty
          console.warn('API returned no questions, using mock data');
          
          // For scenarios, don't use mock data since it's not the right format
          if (settings.category === 'scenarios') {
            setError('No scenario questions found. Please try a different category or check if scenario questions have been added to the database.');
            setQuestions([]);
          } else {
            // For mixed difficulty, use all mock questions
            if (settings.difficulty === 'mixed') {
              setQuestions(mockQuestions);
            } else {
              // For specific difficulty, filter mock questions to match
              const diffLevel = difficultyMap[settings.difficulty];
              const filteredMocks = mockQuestions.filter(q => 
                !diffLevel || q.difficulty === 'easy' || q.difficulty === 'medium'
              );
              setQuestions(filteredMocks.length > 0 ? filteredMocks : mockQuestions);
            }
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch questions:', err);
        setError('Failed to fetch questions. Using mock data instead.');
        
        // Use filtered mock questions based on the selected difficulty and category
        if (settings.difficulty === 'mixed') {
          setQuestions(mockQuestions);
        } else {
          const diffLevel = settings.difficulty;
          const filteredMocks = mockQuestions.filter(q => 
            q.difficulty === diffLevel || !diffLevel
          );
          setQuestions(filteredMocks.length > 0 ? filteredMocks : mockQuestions);
        }
        
        setLoading(false);
      }
    };
    
    fetchQuestions();
  }, [settings, isAuthenticated, user]);
  
  // Ensure user is logged in to backend when authenticated
  useEffect(() => {
    const ensureBackendLogin = async () => {
      if (isAuthenticated && user) {
        try {
          const localUser = authService.getCurrentUser();
          if (!localUser) {
            // Login to our backend with Auth0 user
            const accessToken = await getAccessTokenSilently({
              authorizationParams: {
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
              }
            });
            await authService.loginWithAuth0(user, accessToken);
            console.log('✅ User logged in to backend');
          }
        } catch (error) {
          console.error('Failed to login to backend:', error);
        }
      }
    };
    
    ensureBackendLogin();
  }, [isAuthenticated, user, getAccessTokenSilently]);
  
  // Only access current question if questions are loaded and not empty
  const currentQuestion = (questions && questions.length > 0) ? questions[gameState.currentQuestionIndex] : null;

  // Debug logging for question display (only if question is not loaded yet)
  if (!currentQuestion) {
    console.log('⏳ Waiting for question to load...', {
      questionsLoaded: questions && questions.length > 0,
      currentIndex: gameState.currentQuestionIndex,
      questionsCount: questions?.length || 0
    });
  }

  // Debug the conditional rendering (keeping for consistency but not used in JSX)
  const shouldShowArrow = gameState.selectedAnswer !== null && currentQuestion?.bibleReference;

  // This is now handled directly in JSX with currentQuestion?.bibleReference
  
  // Timer effect
  useEffect(() => {
    // Don't run timer if game is not playing, loading, or user has already selected an answer
    if (gameState.gameStatus !== 'playing' || loading || gameState.selectedAnswer !== null) return;
    
    const timer = setInterval(() => {
      setGameState((prev: any) => {
        if (prev.timeRemaining <= 0) {
          // Time's up, but only move to next question if autoProgress is enabled
          if (settings.autoProgress) {
            return handleTimeout();
          } else {
            // Just mark as timed out but don't progress automatically
            return {
              ...prev,
              timeRemaining: 0,
              timedOut: true
            };
          }
        }
        return {
          ...prev,
          timeRemaining: prev.timeRemaining - 1
        };
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [gameState.gameStatus, gameState.currentQuestionIndex, gameState.selectedAnswer, loading, settings.autoProgress]);
  
  const handleTimeout = (): any => {
    // Prevent errors when question hasn't loaded yet
    if (!currentQuestion) {
      return {
        ...gameState,
        timeRemaining: 0,
        timedOut: true
      };
    }

    // If we're in manual mode, just mark as timed out but don't auto-progress
    if (!settings.autoProgress) {
      return {
        ...gameState,
        timeRemaining: 0,
        timedOut: true
      };
    }

    // Handle auto-progression - timeout means wrong answer, so reset streak
    const newAnswers = [
      ...gameState.answers,
      {
        questionId: currentQuestion.id,
        userAnswer: '',
        isCorrect: false,
        pointsEarned: 0,
        timeRemaining: 0
      }
    ];
    
    if (gameState.currentQuestionIndex < questions.length - 1) {
      return {
        ...gameState,
        currentQuestionIndex: gameState.currentQuestionIndex + 1,
        timeRemaining: settings.timePerQuestion,
        answers: newAnswers,
        selectedAnswer: null,
        isCorrect: null,
        timedOut: false,
        currentStreak: 0 // Reset streak on timeout
      };
    } else {
      // Game over
      return {
        ...gameState,
        gameStatus: 'finished',
        answers: newAnswers,
        currentStreak: 0
      };
    }
  };
  
  const selectAnswer = (answer: string) => {
    if (gameState.selectedAnswer !== null) return; // Prevent multiple selections
    if (!currentQuestion) return; // Prevent errors when question hasn't loaded yet

    const isCorrect = answer === currentQuestion.correctAnswer;
    
    setGameState({
      ...gameState,
      selectedAnswer: answer,
      isCorrect: isCorrect
    });
    
    // If auto-progression is enabled, move to next question after delay
    if (settings.autoProgress) {
      // Store the timeout reference so we can cancel it if needed
      autoProgressTimeoutRef.current = setTimeout(() => {
        submitAnswer(answer);
      }, 4000); // 4 seconds - enough time to read result and decide to open chat
    }
    // Otherwise, user will click the Next button manually
  };
  
  // Handle chat opening - cancel auto-progression
  const handleChatOpen = () => {
    setIsChatOpen(true);
    // Cancel any pending auto-progression
    if (autoProgressTimeoutRef.current) {
      clearTimeout(autoProgressTimeoutRef.current);
      autoProgressTimeoutRef.current = null;
    }
  };

  const handleChatClose = () => {
    setIsChatOpen(false);
  };

  const submitAnswer = (answer: string) => {
    // Prevent errors when question hasn't loaded yet
    if (!currentQuestion) {
      console.warn('submitAnswer called but currentQuestion is null');
      return;
    }

    // Clear any pending auto-progression timeout
    if (autoProgressTimeoutRef.current) {
      clearTimeout(autoProgressTimeoutRef.current);
      autoProgressTimeoutRef.current = null;
    }

    // Reset chat state when moving to next question
    setIsChatOpen(false);

    const isCorrect = answer === currentQuestion.correctAnswer;
    let pointsEarned = 0;
    let resultText = '';
    
    // Update streak
    const newStreak = isCorrect ? gameState.currentStreak + 1 : 0;
    const newMaxStreak = Math.max(newStreak, gameState.maxStreak);
    
    // Handle scenario questions differently
    if (currentQuestion.type === 'scenario' && currentQuestion.scenarioDetails) {
      // Find the selected outcome
      const selectedOutcome = currentQuestion.scenarioDetails.outcomes.find(
        (outcome: any) => outcome.choiceText === answer
      );
      
      if (selectedOutcome) {
        pointsEarned = selectedOutcome.points || 0;
        resultText = selectedOutcome.resultText || '';
      }
    } else {
      // Use our new scoring system for regular questions
      const questionDifficulty = settings.difficulty === 'mixed' ? currentQuestion.difficulty : settings.difficulty;
      const scoreCalc = calculateAnswerPoints(
        questionDifficulty,
        gameState.timeRemaining,
        newStreak,
        isCorrect
      );
      pointsEarned = scoreCalc.totalPoints;
    }
    
    const newScore = gameState.score + pointsEarned;
    
    const newAnswers = [
      ...gameState.answers,
      {
        questionId: currentQuestion.id,
        userAnswer: answer,
        isCorrect,
        pointsEarned,
        resultText,
        timeRemaining: gameState.timeRemaining,
        difficulty: currentQuestion.difficulty,
        type: currentQuestion.type || 'regular'
      }
    ];
    
    // Individual answer tracking not needed - will submit final results at game end
    
    if (gameState.currentQuestionIndex < questions.length - 1) {
      setGameState({
        ...gameState,
        currentQuestionIndex: gameState.currentQuestionIndex + 1,
        score: newScore,
        timeRemaining: settings.timePerQuestion,
        answers: newAnswers,
        selectedAnswer: null,
        isCorrect: null,
        timedOut: false,
        currentStreak: newStreak,
        maxStreak: newMaxStreak
      });
    } else {
      // Game over
      setGameState({
        ...gameState,
        score: newScore,
        gameStatus: 'finished',
        answers: newAnswers,
        timedOut: false,
        currentStreak: newStreak,
        maxStreak: newMaxStreak
      });
      
      // Submit game results to backend if user is authenticated
      const submitAndNavigate = async () => {
        if (isAuthenticated && user) {
          try {
            // Map frontend difficulty to backend format
            const difficultyMap: Record<string, string> = {
              'easy': 'beginner',
              'medium': 'intermediate', 
              'hard': 'advanced',
              'mixed': 'intermediate'
            };

            const gameResult = {
              pointsEarned: newScore,
              correctAnswers: newAnswers.filter(a => a.isCorrect).length,
              totalQuestions: questions.length,
              difficulty: difficultyMap[settings.difficulty] || 'intermediate',
              bestStreak: newMaxStreak
            };

            console.log('📊 Submitting game results:', gameResult);
            const response = await gameService.submitGameResults(gameResult);
            console.log('✅ Game results submitted successfully:', response);
          } catch (error) {
            console.error('❌ Failed to submit game results:', error);
            // Continue to results page even if submission fails
          }
        }

        // Navigate to results page
        navigate('/results', { 
          state: { 
            score: newScore, 
            totalQuestions: questions.length,
            answers: newAnswers,
            questions,
            maxStreak: newMaxStreak,
            difficulty: settings.difficulty,
            // gameSessionId removed - not using sessions
          } 
        });
      };

      submitAndNavigate();
    }
  };
  
  const getButtonClass = (option: string) => {
    if (gameState.selectedAnswer === null) {
      return "question-option";
    }
    
    if (gameState.selectedAnswer === option) {
      return option === currentQuestion.correctAnswer 
        ? "question-option correct-answer" 
        : "question-option wrong-answer";
    }
    
    if (option === currentQuestion.correctAnswer && gameState.selectedAnswer !== option) {
      return "question-option correct-answer";
    }
    
    return "question-option";
  };
  
  if (loading) {
    return (
      <div className="trivia-game-container">
        <div className="loading-container">
          <div className="loading-scroll">📜</div>
          <h2>Loading Questions...</h2>
          <p>Searching the scriptures...</p>
        </div>
      </div>
    );
  }
  
  if (error && questions.length === 0) {
    return (
      <div className="trivia-game-container">
        <div className="error-container">
          <h2>Error Loading Questions</h2>
          <p>{error}</p>
          <button
            onClick={() => navigate('/')}
            className="btn btn-secondary"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }
  
  if (!currentQuestion) {
    return (
      <div className="trivia-game-container">
        <div className="error-container">
          <h2>No questions available</h2>
          <button
            onClick={() => navigate('/')}
            className="btn btn-secondary"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  // Calculate time remaining as a percentage
  const timePercentage = (gameState.timeRemaining / settings.timePerQuestion) * 100;
  
  return (
    <div className="trivia-game-container">
      {/* Home Return Button */}
      <div className="home-return-button-container">
        <button 
          className="home-return-button"
          onClick={() => {
            // Directly navigate to home without confirmation
            navigate('/');
          }}
        >
          <span className="home-text">← Home</span>
        </button>
      </div>
      
      {/* Desktop Score Display - only visible on desktop */}
      <div className="desktop-score-display">
        <span>Score</span>
        <div className="score-box">{gameState.score}</div>
      </div>
      
      {/* Decorative elements */}
      <div className="trivia-decoration trivia-decoration-1"></div>
      <div className="trivia-decoration trivia-decoration-2"></div>
      <div className="trivia-decoration trivia-decoration-3"></div>
      <div className="trivia-star trivia-star-1"></div>
      <div className="trivia-star trivia-star-2"></div>
      <div className="trivia-star trivia-star-3"></div>
      
      <div className="trivia-header">
        <div className="header-top-row">
          <div className="question-counter">
            <span>Question</span>
            <div className="counter-box">
              {gameState.currentQuestionIndex + 1}/{questions.length}
            </div>
          </div>
          
          <div className="score-display">
            <span>Score</span>
            <div className="score-box">{gameState.score}</div>
          </div>
        </div>
      </div>

      <div className="timer-container">
        <div className="timer-bar">
          <div 
            className={`timer-progress ${gameState.timedOut ? 'timed-out' : ''}`}
            style={{ width: `${timePercentage}%` }}
          ></div>
        </div>
        <div className={`timer-display ${gameState.timedOut ? 'timed-out' : ''}`}>
          {gameState.timedOut ? "Time's Up!" : `${gameState.timeRemaining}s`}
        </div>
      </div>
      
      <div className="question-card">
        <div className="question-content">
          {currentQuestion ? (
            <h2 className="question-text">{currentQuestion.text}</h2>
          ) : (
            <h2 className="question-text loading-text">Loading question...</h2>
          )}
          
          {currentQuestion && currentQuestion.type === 'scenario' && currentQuestion.scenarioDetails && (
            <div className="scenario-container">
              <div className="scenario-scroll-icon">📜</div>
              <div className="scenario-story">
                <p>{currentQuestion.scenarioDetails.story}</p>
              </div>
              <div className="scenario-prompt">
                <p>What would you do?</p>
              </div>
            </div>
          )}
          
          <div className="options-container">
            {currentQuestion && currentQuestion.options ? (
              currentQuestion.options.map((option: any, index: number) => (
                <button
                  key={index}
                  onClick={() => selectAnswer(typeof option === 'object' && option.text ? option.text : option)}
                  className={getButtonClass(typeof option === 'object' && option.text ? option.text : option)}
                  disabled={gameState.selectedAnswer !== null || (!settings.autoProgress && gameState.timedOut)}
                >
                  {typeof option === 'object' && option.text ? option.text : option}
                </button>
              ))
            ) : (
              <div className="loading-options">
                <button disabled className="loading-button">Loading options...</button>
                <button disabled className="loading-button">Loading options...</button>
                <button disabled className="loading-button">Loading options...</button>
                <button disabled className="loading-button">Loading options...</button>
              </div>
            )}
          </div>
          
          {gameState.selectedAnswer !== null && currentQuestion.type === 'scenario' && currentQuestion.scenarioDetails && (
            <div className="scenario-result">
              {(() => {
                // Find the selected outcome
                const selectedOutcome = currentQuestion.scenarioDetails.outcomes.find(
                  (outcome: any) => outcome.choiceText === gameState.selectedAnswer
                );
                
                if (selectedOutcome) {
                  return (
                    <>
                      <div className="scenario-result-points">
                        <span className="points-label">Points earned:</span>
                        <span className="points-value">{selectedOutcome.points}</span>
                      </div>
                      <div className="scenario-result-text">
                        <p>{selectedOutcome.resultText}</p>
                      </div>
                      {selectedOutcome.isRecommended && (
                        <div className="recommended-choice">
                          <span>✓</span> Biblically recommended choice
                        </div>
                      )}
                    </>
                  );
                }
                return null;
              })()}
            </div>
          )}
          
          {/* Three-column layout with Save to Favorites, Bible Reference, and Next Question */}
          {gameState.selectedAnswer !== null && currentQuestion?.bibleReference && (
            <div className="three-column-actions-row">
              {/* Column 1: Save to Favorites button - DISABLED until API is implemented */}
              <div className="column-left">
                {/* 
                  TODO: Re-enable SaveToFavorites component when:
                  1. Favorites API endpoints are working on production server
                  2. favoriteService.ts is updated to use real API endpoints
                  3. Cross-platform sync is tested between web and iOS
                  
                  Current status: Component exists but uses mock data
                  Files ready for integration: favorites-controller.js, favorites-routes.js
                */}
                {/* <SaveToFavorites 
                  type="question"
                  questionId={currentQuestion.id}
                  verse={currentQuestion.bibleReference ? (
                    typeof currentQuestion.bibleReference === 'string'
                      ? {
                          book: currentQuestion.bibleReference.split(' ')[0],
                          chapter: parseInt(currentQuestion.bibleReference.split(' ')[1].split(':')[0]),
                          verses: currentQuestion.bibleReference.split(':')[1],
                          text: `"${currentQuestion.correctAnswer}" - ${currentQuestion.bibleReference}`
                        }
                      : {
                          ...currentQuestion.bibleReference,
                          text: `"${currentQuestion.correctAnswer}" - ${currentQuestion.bibleReference.book} ${currentQuestion.bibleReference.chapter}:${currentQuestion.bibleReference.verses}`
                        }
                  ) : undefined}
                /> */}
              </div>
              
              {/* Column 2: Bible Reference with integrated Next button */}
              <div className="column-middle">
                <div className="bible-reference-with-next">
                  <div className="bible-reference">
                    <div className="reference-icon">📖</div>
                    <div className="reference-content">
                      <div className="reference-label">BIBLE REFERENCE</div>
                      <div className="reference-text">
                        {typeof currentQuestion.bibleReference === 'string' 
                          ? currentQuestion.bibleReference 
                          : `${currentQuestion.bibleReference.book} ${currentQuestion.bibleReference.chapter}:${currentQuestion.bibleReference.verses}`
                        }
                      </div>
                    </div>
                    {gameState.selectedAnswer && (
                      <div className="reference-shine"></div>
                    )}
                  </div>
                  <button 
                    className="integrated-next-button"
                    onClick={() => submitAnswer(gameState.selectedAnswer || "")}
                    aria-label="Next question"
                  >
                    →
                  </button>
                </div>
              </div>
              
              {/* Column 3: Empty for balance */}
              <div className="column-right">
              </div>
            </div>
          )}
          
          {/* Auto-progression paused indicator */}
          {settings.autoProgress && isChatOpen && gameState.selectedAnswer && (
            <div className="auto-progress-paused">
              <Pause className="pause-icon" size={20} />
              <span className="pause-text">Auto-progression paused while chat is open</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="game-controls">
        {/* Show Next Question button when timer runs out in manual mode */}
        {!settings.autoProgress && gameState.timedOut && !gameState.selectedAnswer && (
          <div className="three-column-actions-row timed-out-row">
            <div className="column-left"></div>
            <div className="column-middle">
              <button 
                className="next-question-button timed-out-button"
                onClick={() => submitAnswer("")}
              >
                Skip to Next Question <span className="next-arrow">→</span>
              </button>
            </div>
            <div className="column-right"></div>
          </div>
        )}
      </div>
      
      {/* ChatGPT integration is now active! */}
      {/* Chat Widget - only show after wrong answer */}
      <ChatWidget
        currentQuestion={
          gameState.selectedAnswer !== null && gameState.isCorrect === false
            ? {
                question: currentQuestion.text,
                correctAnswer: currentQuestion.correctAnswer,
                userAnswer: gameState.selectedAnswer,
                category: currentQuestion.category
              }
            : undefined
        }
        isVisible={gameState.selectedAnswer !== null && gameState.isCorrect === false}
        onChatOpen={handleChatOpen}
        onChatClose={handleChatClose}
      />
    </div>
  );
};

export default TriviaGamePage;